import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Calendar as CalendarPicker } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarIcon, Loader2, X } from 'lucide-react';
import { format } from 'date-fns';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six_monthly', label: '6 Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One Time' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

/**
 * Shared create/edit expense dialog used by both the Dashboard (edit) and
 * Expenses (add/edit) pages. The parent owns the form state and submit
 * behaviour; this component only renders the fields.
 */
export default function ExpenseFormDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  formData,
  setFormData,
  categories,
  onSubmit,
  isSubmitting = false,
  testIdPrefix = 'expense',
}) {
  return (
    <Dialog open={open} onOpenChange={isSubmitting ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { if (isSubmitting) { e.preventDefault(); return; } onSubmit(e); }} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Electricity Bill"
                className="rounded-[4px]"
                data-testid={`${testIdPrefix}-name-input`}
              />
            </div>

            <div className="space-y-2">
              <Label>Amount (AUD) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="rounded-[4px]"
                data-testid={`${testIdPrefix}-amount-input`}
              />
              <div className="flex items-center space-x-2 mt-2">
                <Checkbox
                  id={`${testIdPrefix}-gst-inclusive`}
                  checked={formData.gst_inclusive}
                  onCheckedChange={(checked) => setFormData({ ...formData, gst_inclusive: checked })}
                  data-testid={`${testIdPrefix}-gst-inclusive-checkbox`}
                />
                <label
                  htmlFor={`${testIdPrefix}-gst-inclusive`}
                  className="text-sm text-stone-600 cursor-pointer"
                >
                  GST Inclusive (10%)
                </label>
              </div>
              {formData.gst_inclusive && formData.amount && (
                <p className="text-xs text-stone-500">
                  GST component: {formatCurrency(parseFloat(formData.amount) / 11)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="rounded-[4px]" data-testid={`${testIdPrefix}-category-select`}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={formData.expense_type}
                onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
              >
                <SelectTrigger className="rounded-[4px]" data-testid={`${testIdPrefix}-type-select`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData({ ...formData, frequency: value })}
              >
                <SelectTrigger className="rounded-[4px]" data-testid={`${testIdPrefix}-frequency-select`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-[4px]"
                    data-testid={`${testIdPrefix}-due-date-btn`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.due_date ? format(formData.due_date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="single"
                    selected={formData.due_date}
                    onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>
                End date <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <div className="flex gap-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 justify-start text-left font-normal rounded-[4px]"
                      data-testid={`${testIdPrefix}-end-date-btn`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.end_date ? format(formData.end_date, 'PPP') : 'No end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={formData.end_date || undefined}
                      onSelect={(date) => date && setFormData({ ...formData, end_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {formData.end_date && (
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-[4px] px-2"
                    title="Clear end date"
                    onClick={() => setFormData({ ...formData, end_date: null })}
                    data-testid={`${testIdPrefix}-end-date-clear`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-500">Recurrence stops after this date.</p>
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                className="rounded-[4px] resize-none"
                rows={2}
                data-testid={`${testIdPrefix}-notes-input`}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="rounded-[4px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px] disabled:opacity-70"
              data-testid={`${testIdPrefix}-submit-btn`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
